import API_BASE_URL from '../config';
import { getToken } from './authService';
import { getCategoryPath, getCategoryIdStr } from './campaignService';

let loanCampaignPromise = null;

export async function getLoanCampaign(forceRefresh = false) {
  if (forceRefresh) loanCampaignPromise = null;
  if (loanCampaignPromise) return loanCampaignPromise;

  const url = forceRefresh
    ? `${API_BASE_URL}/campaigns/loan?_t=${Date.now()}`
    : `${API_BASE_URL}/campaigns/loan`;

  loanCampaignPromise = fetch(url)
    .then(async (res) => {
      if (!res.ok) throw new Error('Failed to fetch loan campaign');
      const data = await res.json();
      return data.campaign || null;
    })
    .catch((err) => {
      loanCampaignPromise = null;
      console.error('Error fetching loan campaign:', err);
      return null;
    });

  return loanCampaignPromise;
}

export async function updateLoanCampaign(campaign) {
  const token = getToken();
  const res = await fetch(`${API_BASE_URL}/campaigns/loan`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    body: JSON.stringify(campaign)
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || 'Dështoi ruajtja e konfigurimit të kredisë');
  }

  const result = await res.json();
  loanCampaignPromise = null; // Invalidate cache

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('loanCampaignUpdated', {
      detail: { campaign: result.campaign || campaign }
    }));
  }

  return result;
}

export async function uploadLoanLogo(file) {
  const token = getToken();
  const formData = new FormData();
  formData.append('loanLogo', file);

  const res = await fetch(`${API_BASE_URL}/campaigns/loan/upload-logo`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`
    },
    body: formData
  });

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    throw new Error(errorData.error || 'Dështoi ngarkimi i logos së partnerit');
  }

  return await res.json();
}

/**
 * Checks whether a given product matches a loan campaign rule.
 * Identical targeting logic to sticker rules (SKUs + Category hierarchy).
 */
export function matchesLoanRule(product, rule, categoryTree = []) {
  if (!product || !rule || !rule.enabled) return false;

  const productSku = String(product.sku || product.kodi_i_produktit || '').trim().toUpperCase();

  // 1. Direct SKU match takes precedence
  if (Array.isArray(rule.skus) && rule.skus.length > 0) {
    const hasSkuMatch = rule.skus.some(
      s => String(s || '').trim().toUpperCase() === productSku
    );
    if (hasSkuMatch) return true;
  }

  // 2. Category match
  if (Array.isArray(rule.categoryIds) && rule.categoryIds.length > 0) {
    const targetCatIds = new Set(rule.categoryIds.map(id => String(id).trim()));

    const pCatId = getCategoryIdStr(product.categoryId || product.category_id || (product.category && product.category.id));
    const pCategoryIds = Array.isArray(product.categoryIds)
      ? product.categoryIds.map(String)
      : [];

    if (pCatId && targetCatIds.has(pCatId)) return true;
    if (pCategoryIds.some(cid => targetCatIds.has(String(cid)))) return true;

    // Check ancestor categories if includeSubcategories is enabled
    if (rule.includeSubcategories !== false && pCatId && Array.isArray(categoryTree) && categoryTree.length > 0) {
      const path = getCategoryPath(pCatId, categoryTree);
      if (Array.isArray(path) && path.some(node => targetCatIds.has(getCategoryIdStr(node?.id || node)))) {
        return true;
      }
    }

    // If rule has categoryIds specified and this product didn't match any, return false
    return false;
  }

  // 3. If neither SKUs nor CategoryIds are specified, rule acts as catch-all (matches all products)
  const hasNoRestrictions = (!rule.skus || rule.skus.length === 0) &&
                            (!rule.categoryIds || rule.categoryIds.length === 0);
  return hasNoRestrictions;
}

/**
 * Helper to format price with separators (e.g. 15,000 L)
 */
export function formatLoanPrice(amount) {
  const n = Math.round(Number(amount) || 0);
  return `${n.toLocaleString('sq-AL')} L`;
}

/**
 * Helper to replace tokens like {monthly}, {months}, {partner}, {price}
 */
export function formatLoanTemplate(template, vars = {}) {
  if (!template || typeof template !== 'string') return '';
  return template
    .replace(/\{monthly\}/g, vars.monthly !== undefined ? String(vars.monthly) : '')
    .replace(/\{months\}/g, vars.months !== undefined ? String(vars.months) : '')
    .replace(/\{partner\}/g, vars.partner !== undefined ? String(vars.partner) : '')
    .replace(/\{price\}/g, vars.price !== undefined ? String(vars.price) : '');
}

/**
 * Evaluates whether a product is eligible for loan and calculates monthly terms
 */
export function evaluateProductLoan(product, campaign, categoryTree = []) {
  if (!product || !campaign || !campaign.enabled) return null;

  const rules = Array.isArray(campaign.rules) ? campaign.rules : [];
  if (rules.length === 0) return null;

  // Determine effective product price
  const origPrice = Number(product.price || 0);
  const salePrice = product.salePrice !== undefined && product.salePrice !== null ? Number(product.salePrice) : null;
  const offerPrice = product.offerPrice !== undefined && product.offerPrice !== null ? Number(product.offerPrice) : null;

  let effectivePrice = origPrice;
  if (offerPrice && offerPrice > 0 && offerPrice < origPrice) {
    effectivePrice = offerPrice;
  } else if (salePrice && salePrice > 0 && salePrice < origPrice) {
    effectivePrice = salePrice;
  }

  if (effectivePrice <= 0) return null;

  // Find matching rule: prioritize SKU-specific matches over general category matches
  let matchingRule = null;
  const productSku = String(product.sku || product.kodi_i_produktit || '').trim().toUpperCase();

  // Check SKU matches first
  if (productSku) {
    matchingRule = rules.find(r => r.enabled && Array.isArray(r.skus) && r.skus.includes(productSku));
  }

  // Fallback to first matching rule (category or all)
  if (!matchingRule) {
    matchingRule = rules.find(r => r.enabled && matchesLoanRule(product, r, categoryTree));
  }

  if (!matchingRule) return null;

  // Check min price
  const minPrice = Number(matchingRule.minPrice) || 0;
  if (minPrice > 0 && effectivePrice < minPrice) return null;

  // Calculate terms
  const rawTerms = Array.isArray(matchingRule.terms) && matchingRule.terms.length > 0
    ? matchingRule.terms.filter(t => t && t.enabled)
    : [
        { months: 12, enabled: true, interestRate: 0, label: '12 Muaj' },
        { months: 36, enabled: true, interestRate: 0, label: '36 Muaj' }
      ];

  if (rawTerms.length === 0) return null;

  const partnerName = matchingRule.partnerName || campaign.partnerName || 'Iute Pay';
  const partnerLogo = matchingRule.partnerLogo || campaign.partnerLogo || '';

  const calculatedTerms = rawTerms.map(t => {
    const months = Number(t.months) || 12;
    const interest = Number(t.interestRate) || 0;
    const totalAmount = interest > 0 ? effectivePrice * (1 + interest / 100) : effectivePrice;
    const monthlyAmount = Math.round(totalAmount / months);
    return {
      months,
      label: t.label || `${months} Muaj`,
      interestRate: interest,
      totalAmount,
      monthlyAmount,
      monthlyFormatted: formatLoanPrice(monthlyAmount),
      totalFormatted: formatLoanPrice(totalAmount)
    };
  });

  // Sort terms by months ascending
  calculatedTerms.sort((a, b) => a.months - b.months);

  // Term 12 and Term 36 specific lookups
  const term12 = calculatedTerms.find(t => t.months === 12);
  const term36 = calculatedTerms.find(t => t.months === 36);
  const lowestMonthlyTerm = calculatedTerms.reduce((min, t) => t.monthlyAmount < min.monthlyAmount ? t : min, calculatedTerms[0]);
  const highestMonthlyTerm = calculatedTerms.reduce((max, t) => t.monthlyAmount > max.monthlyAmount ? t : max, calculatedTerms[0]);

  // Card Info
  const cardConfig = matchingRule.card || {};
  let cardTerm = lowestMonthlyTerm;
  if (cardConfig.selectedTerm === 12 && term12) {
    cardTerm = term12;
  } else if (cardConfig.selectedTerm === 36 && term36) {
    cardTerm = term36;
  } else if (cardConfig.selectedTerm === 'highest') {
    cardTerm = highestMonthlyTerm;
  }

  const cardVars = {
    monthly: cardTerm.monthlyFormatted,
    months: cardTerm.months,
    partner: partnerName,
    price: formatLoanPrice(effectivePrice)
  };

  const cardBadgeText = formatLoanTemplate(cardConfig.badgeText || 'Nga {monthly}/muaj me {months} këste', cardVars);
  const cardSubpriceText = formatLoanTemplate(cardConfig.subpriceText || 'nga {monthly}/muaj ({months} këste)', cardVars);

  // Product View Info
  const pvConfig = matchingRule.productView || {};
  let pvDefaultTerm = calculatedTerms.find(t => t.months === Number(pvConfig.defaultSelectedTerm)) || term12 || lowestMonthlyTerm;

  const pvVars = {
    monthly: pvDefaultTerm.monthlyFormatted,
    months: pvDefaultTerm.months,
    partner: partnerName,
    price: formatLoanPrice(effectivePrice)
  };

  const pvBadgeText = formatLoanTemplate(pvConfig.badgeText || 'Me Këste: nga {monthly}/muaj ({months} muaj)', pvVars);
  const pvBoxTitle = formatLoanTemplate(pvConfig.boxTitle || 'Mundësi Blerje me Këste 0% Interes', pvVars);
  const pvBoxSubtitle = formatLoanTemplate(pvConfig.boxSubtitle || 'Paguaj me këste të favorshme në bashkëpunim me {partner}', pvVars);

  return {
    isEligible: true,
    ruleId: matchingRule.id,
    ruleName: matchingRule.name,
    partnerName,
    partnerLogo,
    effectivePrice,
    effectivePriceFormatted: formatLoanPrice(effectivePrice),
    terms: calculatedTerms,
    term12,
    term36,
    lowestMonthlyTerm,
    // Card Settings evaluated
    card: {
      enabled: cardConfig.enabled !== false,
      displayMode: cardConfig.displayMode || 'badge', // 'badge' | 'subprice' | 'both'
      selectedTerm: cardTerm,
      badgeText: cardBadgeText,
      subpriceText: cardSubpriceText,
      position: cardConfig.position || 'top-left',
      offsetX: Number(cardConfig.offsetX !== undefined ? cardConfig.offsetX : 10),
      offsetY: Number(cardConfig.offsetY !== undefined ? cardConfig.offsetY : 10),
      bgColor: cardConfig.bgColor || '#007da5',
      textColor: cardConfig.textColor || '#ffffff',
      borderRadius: Number(cardConfig.borderRadius) || 6,
      fontSize: Number(cardConfig.fontSize) || 11,
      fontWeight: Number(cardConfig.fontWeight) || 700,
      shadow: cardConfig.shadow !== false
    },
    // Product View Settings evaluated
    productView: {
      enabled: pvConfig.enabled !== false,
      showBadge: Boolean(pvConfig.showBadge),
      badgePosition: pvConfig.badgePosition || 'top-left',
      badgeOffsetX: Number(pvConfig.badgeOffsetX !== undefined ? pvConfig.badgeOffsetX : 10),
      badgeOffsetY: Number(pvConfig.badgeOffsetY !== undefined ? pvConfig.badgeOffsetY : 10),
      badgeBgColor: pvConfig.badgeBgColor || '#007da5',
      badgeTextColor: pvConfig.badgeTextColor || '#ffffff',
      badgeText: pvBadgeText,
      showBox: pvConfig.showBox !== false,
      boxTitle: pvBoxTitle,
      boxSubtitle: pvBoxSubtitle,
      defaultSelectedTerm: pvDefaultTerm,
      showTermsSelector: pvConfig.showTermsSelector !== false,
      note: pvConfig.note || 'Apliko lehtësisht me kartë identiteti në arkë ose online gjatë porosisë.'
    }
  };
}
